// Editorial presentational sections for the site-intelligence dossier.
//
// Built on the shared REPORT ("Blue Ridge") palette — the same visual system as
// the Integra portfolio and the print briefings — so a recipient flipping
// between an Atwater portfolio, a briefing, and this dossier reads one product.
// Serif titles, mono eyebrows, hairline rules, sand/amber washes on cool paper.

import { type CSSProperties, type ReactNode, useState } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { fetchClusterShareToken } from '../../lib/api';
import type { SiteDossier } from './keipDossier';

const VALID_SHARE_TOKEN_RE = /^[0-9a-f]{48}$/i;

// ---- Type system --------------------------------------------------------
export const SERIF = 'Georgia, "Iowan Old Style", "Times New Roman", serif';
export const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
export const MONO = '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';
export const RULE = 'rgba(46,56,64,0.13)';
export const RULE_SOFT = 'rgba(46,56,64,0.07)';

// ---- Shared atoms -------------------------------------------------------
export function Eyebrow({ children, color = REPORT.accent, style }: { children: ReactNode; color?: string; style?: CSSProperties }) {
  return (
    <div style={{
      fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: '0.16em',
      textTransform: 'uppercase', color, ...style,
    }}>{children}</div>
  );
}

export function SectionHead({ eyebrow, title, aside }: { eyebrow: string; title: string; aside?: ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginTop: 8 }}>
        <h2 style={{
          margin: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 26,
          color: REPORT.heading, letterSpacing: '-0.01em',
        }}>{title}</h2>
        {aside && <div style={{ fontFamily: SANS, fontSize: 12, color: REPORT.secondary, flexShrink: 0 }}>{aside}</div>}
      </div>
      <div style={{ height: 1, background: REPORT.highlight, opacity: 0.4, marginTop: 12 }} />
    </div>
  );
}

const SECTION: CSSProperties = { marginTop: 56 };

// ---- Masthead -----------------------------------------------------------
export function DossierMasthead({ d, slug }: { d: SiteDossier; slug: string }) {
  const [copied, setCopied] = useState(false);
  // Build the outside-shareable URL: a recipient's URL already carries the
  // token, so copy it as-is; the owner (no token in the URL) fetches the
  // cluster's permanent share token and builds the tokenized link.
  const shareUrl = async (): Promise<string> => {
    const existing = new URLSearchParams(window.location.search).get('token');
    if (existing && VALID_SHARE_TOKEN_RE.test(existing)) return window.location.href;
    try {
      const token = await fetchClusterShareToken(slug);
      return `${window.location.origin}/cluster/${slug}?token=${token}`;
    } catch {
      return window.location.href; // fall back to the bare URL
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(await shareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* older browsers — user can copy manually */ }
  };
  return (
    <header style={{
      background: REPORT.heading, borderRadius: 4, padding: '11px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
        <img src="/atwater-logo-light.svg" alt="Atwater Energy" style={{ height: 26, width: 'auto', display: 'block' }} />
        <span style={{
          fontFamily: MONO, fontSize: 9.5, fontWeight: 500, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: REPORT.patina, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
          borderLeft: '1px solid rgba(212,152,128,0.28)', paddingLeft: 16,
        }}>
          {d.eyebrow} · {d.subtitle.split('·').slice(-1)[0].trim()}
        </span>
      </div>
      <button type="button" onClick={copy} style={{
        marginLeft: 8, padding: '4px 12px', background: 'transparent',
        border: '1px solid rgba(212,152,128,0.4)', color: REPORT.patina,
        borderRadius: 3, cursor: 'pointer', fontSize: 11.5, fontFamily: SANS, whiteSpace: 'nowrap',
      }}>{copied ? 'Link copied' : 'Share'}</button>
    </header>
  );
}

// ---- Hero (aerial + fact strip) ----------------------------------------
export function DossierHero({ d }: { d: SiteDossier }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        position: 'relative', borderRadius: 6, overflow: 'hidden',
        border: `1px solid ${RULE}`, aspectRatio: '16 / 7', minHeight: 340,
      }}>
        <picture>
          <source srcSet={d.hero.webp} type="image/webp" />
          <img
            src={d.hero.jpg}
            alt={d.hero.alt}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </picture>
        {/* Scrim: dark from the bottom-left so the title reads on the sky-lit aerial. */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(105deg, rgba(20,26,32,0.82) 0%, rgba(20,26,32,0.55) 34%, rgba(20,26,32,0.08) 62%, rgba(20,26,32,0) 100%)',
        }} />
        <div style={{
          position: 'absolute', left: 0, bottom: 0, padding: '32px 34px', maxWidth: 720,
        }}>
          {/* The masthead above already carries d.eyebrow — the hero leads with
              the play type + location instead of repeating it. */}
          <Eyebrow color={REPORT.patina}>{d.subtitle}</Eyebrow>
          <h1 style={{
            margin: '12px 0 0', fontFamily: SERIF, fontWeight: 400, color: '#F3F1ED',
            fontSize: 'clamp(26px, 4.4vw, 42px)', lineHeight: 1.08, letterSpacing: '-0.015em',
            textShadow: '0 1px 20px rgba(0,0,0,0.35)',
          }}>{d.title}</h1>
          {/* The headline deck — the site's one-line claim, between name and detail. */}
          <p style={{
            margin: '10px 0 0', fontFamily: SERIF, fontWeight: 400, fontStyle: 'italic',
            fontSize: 'clamp(15px, 1.8vw, 19px)', lineHeight: 1.3, color: '#EDE7DC',
            letterSpacing: '-0.005em', textShadow: '0 1px 14px rgba(0,0,0,0.3)',
          }}>{d.hero.headline}</p>
          <p style={{
            margin: '12px 0 0', fontFamily: SANS, fontSize: 14.5, lineHeight: 1.5,
            color: 'rgba(243,241,237,0.86)', maxWidth: 560,
          }}>{d.hero.sub}</p>
        </div>
      </div>
    </div>
  );
}

// ---- Hero facts + live deal, as one restrained block -------------------
// The six site facts sit atop a hairline; the executed-MOU line reads beneath
// as an editorial statement — no alert wash, no pulsing dot, no accent rail.
// The City's intent leads (deal.lede); the exclusivity mechanics trail as a
// quiet footnote.
export function HeroFactsDeal({ d }: { d: SiteDossier }) {
  const { deal } = d;
  return (
    <div style={{ marginTop: 14, border: `1px solid ${RULE}`, borderRadius: 6, overflow: 'hidden', background: REPORT.white }}>
      {/* Facts */}
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {d.heroStats.map((s, i) => (
          <div key={i} style={{ flex: '1 1 180px', padding: '13px 18px', borderLeft: i === 0 ? 'none' : `1px solid ${RULE_SOFT}` }}>
            <div style={{ fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: REPORT.tertiary }}>{s.label}</div>
            <div style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 600, color: REPORT.heading, marginTop: 5 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Deal */}
      <div style={{ borderTop: `1px solid ${RULE}`, padding: '18px 22px', background: REPORT.surface }}>
        <Eyebrow color={REPORT.accent}>Live deal · {deal.status}</Eyebrow>
        <p style={{ margin: '10px 0 0', fontFamily: SERIF, fontSize: 18.5, lineHeight: 1.42, color: REPORT.heading, letterSpacing: '-0.006em', maxWidth: 780 }}>
          {deal.lede}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', marginTop: 11 }}>
          <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: REPORT.body }}>{deal.counterparty}</span>
          <span style={{ fontFamily: SANS, fontSize: 12.5, color: REPORT.secondary }}>· {deal.date}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginTop: 13 }}>
          <span style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: REPORT.tertiary, marginRight: 2 }}>Tracts under MOU</span>
          {deal.tracts.map((t) => (
            <span key={t.id} style={{
              fontFamily: MONO, fontSize: 12, fontWeight: 600, color: REPORT.heading,
              background: REPORT.white, border: `1px solid ${RULE}`, borderRadius: 4, padding: '2px 9px',
            }}>
              {t.id}
              {t.acres && <span style={{ fontWeight: 400, color: REPORT.secondary }}> · {t.acres}</span>}
            </span>
          ))}
        </div>
        <div style={{ fontFamily: SANS, fontSize: 11.5, color: REPORT.tertiary, marginTop: 11, fontStyle: 'italic' }}>{deal.terms}</div>
      </div>
    </div>
  );
}

// ---- Site fact sheet ----------------------------------------------------
export function FactSheet({ d }: { d: SiteDossier }) {
  return (
    <section style={SECTION}>
      <SectionHead eyebrow="Site & Infrastructure" title="Fact sheet" aside="Sources: KY Cabinet for Economic Development · engineer’s estimates" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        {d.factSheet.map((p) => (
          <div key={p.key} style={{
            background: REPORT.white, border: `1px solid ${RULE}`, borderRadius: 5,
            padding: '15px 16px 14px', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: REPORT.heading, marginBottom: 10 }}>{p.title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {p.facts.map((f, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontFamily: SANS, fontSize: 12.5, padding: '3px 0', borderTop: i === 0 ? 'none' : `1px solid ${RULE_SOFT}` }}>
                  <span style={{ color: REPORT.secondary, flexShrink: 0 }}>{f.label}</span>
                  <span style={{ color: REPORT.heading, fontWeight: 600, textAlign: 'right' }}>{f.value}</span>
                </div>
              ))}
            </div>
            {p.note && (
              <div style={{ marginTop: 'auto', paddingTop: 10, fontFamily: SANS, fontSize: 11, lineHeight: 1.45, color: REPORT.tertiary, fontStyle: 'italic' }}>{p.note}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Incentives & designations -----------------------------------------
export function Designations({ d }: { d: SiteDossier }) {
  return (
    <section style={SECTION}>
      <SectionHead eyebrow="Incentives & Designations" title="Why this site clears faster" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px 28px' }}>
        {d.designations.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <svg width="15" height="15" viewBox="0 0 16 16" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden>
              <circle cx="8" cy="8" r="8" fill={REPORT.surface} />
              <path d="M4.5 8.2l2.3 2.3 4.7-4.9" stroke={REPORT.highlight} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <div style={{ fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: REPORT.heading }}>{it.label}</div>
              {it.detail && <div style={{ fontFamily: SANS, fontSize: 11.5, lineHeight: 1.4, color: REPORT.secondary, marginTop: 2 }}>{it.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Data room ----------------------------------------------------------
const DOC_CATEGORIES = ['Site & Survey', 'Engineering', 'Environmental', 'Buildings', 'Financial'] as const;

export function DataRoom({ d }: { d: SiteDossier }) {
  const linked = d.dataRoom.filter((x) => x.url).length;
  return (
    <section style={SECTION}>
      <SectionHead eyebrow="Due-Diligence Package" title="Data room" aside={`${d.dataRoom.length} documents · ${linked} downloadable here`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px 28px' }}>
        {DOC_CATEGORIES.map((cat) => {
          const docs = d.dataRoom.filter((x) => x.category === cat);
          if (!docs.length) return null;
          return (
            <div key={cat}>
              <div style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: REPORT.accent, paddingBottom: 8, borderBottom: `1px solid ${RULE}`, marginBottom: 8 }}>{cat}</div>
              {docs.map((doc, i) => {
                const row = (
                  <>
                    <svg width="12" height="14" viewBox="0 0 12 14" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden>
                      <path d="M1 1h6l4 4v8H1z" fill="none" stroke={REPORT.tertiary} strokeWidth="1" strokeLinejoin="round" />
                      <path d="M7 1v4h4" fill="none" stroke={REPORT.tertiary} strokeWidth="1" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontFamily: SANS, fontSize: 12, lineHeight: 1.35, color: REPORT.body }}>
                      {doc.title}
                      {doc.url
                        ? <span style={{ fontFamily: MONO, fontSize: 10, color: REPORT.accent, marginLeft: 6 }} aria-hidden>↓</span>
                        : <span style={{ fontSize: 10.5, color: REPORT.tertiary, fontStyle: 'italic', marginLeft: 6 }}>on request</span>}
                    </span>
                  </>
                );
                return doc.url ? (
                  <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 0', textDecoration: 'none' }}>
                    {row}
                  </a>
                ) : (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 0' }}>{row}</div>
                );
              })}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---- Footer -------------------------------------------------------------
export function DossierFooter({ d }: { d: SiteDossier }) {
  return (
    <footer style={{ marginTop: 56, paddingTop: 18, borderTop: `1px solid ${RULE}`, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/atwater-logo-dark.svg" alt="Atwater Energy" style={{ height: 20, width: 'auto', opacity: 0.85 }} />
        <span style={{
          fontFamily: SANS, fontSize: 11, color: REPORT.tertiary, whiteSpace: 'nowrap',
          borderLeft: `1px solid ${RULE}`, paddingLeft: 10,
        }}>by RSM Data</span>
      </div>
      <div style={{ fontFamily: SANS, fontSize: 10.5, color: REPORT.tertiary, textAlign: 'right', lineHeight: 1.5, maxWidth: 640 }}>
        Prepared by Atwater Energy, an RSM Data company · Site intelligence · {d.dataAsOf}. Facts compiled from the
        executed MOU, the KY Cabinet for Economic Development build-ready package, and public federal and state
        datasets (FEMA, USGS, EPA, FCC, HIFLD, KGS). Modeled figures are Atwater estimates, not surveyed values.
      </div>
    </footer>
  );
}
